<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Prameny – plány Prahy</h2>
                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/prameny_plany_prahy.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Nejpodrobnější z mnoha verzí plánů tzv. Stabilního
                        katastru. / Zdroj: Archiv Institutu plánování a rozvoje
                        města, Elaborát katastrálního měření 1841–1842 (1856), 1
                        : 720.</span
                    >
                </div>
                <p class="mb-5 font-bold">
                    Historické plány Prahy – významné, avšak ne vždy zcela
                    spolehlivé prameny pro rekonstrukci uličního názvosloví
                    napříč staletími. Text, mimo jiné, upozorňuje na dvě
                    základní úskalí historických plánů – obtížné nebo nepřesné
                    datování a opakované přebírání údajů bez ověření v terénu.
                </p>

                <p class="mb-5">
                    Plány Prahy, ať již rukopisné či tištěné, jsou pro historii
                    ulic důležitým pramenem. Narážíme u nich ovšem na dva
                    základní problémy: 1) Není vždy úplně jasná jejich datace, a
                    i když je plán datován, není jasné, zda odráží stav z doby,
                    do které se hlásí. 2) Tvůrci těchto plánů, zvláště od roku
                    1920, kdy se z Prahy stalo velkoměsto, nemohli vždy ověřit
                    situaci včetně názvů ulic v terénu a mnohé údaje byly
                    přebírány z jednoho vydání do druhého. Následně je uveden
                    přehled základních plánů Prahy, které ve zlomových obdobích
                    zachycují názvy ulic v co nejširší míře a které představují
                    základní pomůcku při sestavování přehledu historie názvů.
                </p>

                <p>18. století</p>
                <p class="mb-5">
                    1769 –
                    <span class="font-bold"
                        >Perspektivní plán Prahy Josefa Daniela Hubera </span
                    >z roku 1769 přináší sice působivý trojrozměrný pohled na
                    Prahu, avšak názvy ulic jsou zde uvedeny pouze částečně,
                    někdy chybně, v této době samozřejmě v němčině
                    (Österreichische Nationalbibliothek, Kartensammlung, sign.
                    FKB P 67,
                    <a
                        href="http://towns.hiu.cas.cz/p_zoom.php?map=Huber"
                        target="_blank"
                        class="article-link"
                        >on-line </a
                    >dostupný na stránkách Historického ústavu AV ČR).
                </p>

                <p class="mb-5">
                    1790/1791 –
                    <span class="font-bold">Hergetův plán Prahy</span>, uložený
                    v Archivu hl. m. Prahy, je prvním geometricky zaměřeným
                    plánem Prahy a vzhledem ke svému měřítku (1 : 1728) přináší
                    podrobný pohled na město ve své době. Není přesně datován,
                    ale vzhledem k dedikaci císaři Leopoldu II. (nastoupil na
                    trůn 1790) a k tomu, že je zde zakreslena kaple Božího těla
                    na dnešním Karlově náměstí (zbořena 1791), zachycuje stav
                    zhruba z přelomu těchto let. Na zaměření plánu se však
                    pracovalo již v osmdesátých letech 18. století. Takřka
                    všechny ulice tehdejšího čerstvě spojeného města Prahy jsou
                    označeny německým názvem. Ty, které označeny nejsou, patrně
                    název (úřední?) neměly. Vyznačena jsou zde i první čísla
                    popisná pražských domů. (Archiv hl. m. Prahy, Sbírka plánů,
                    sign. MAP P 1 B/1, Knižní faksimile: Marek Lašťovka – Jitka
                    Močičková – Barbora Lašťovková – Johana Langerová, Hergetův
                    plán Prahy 1790/1791, Praha 2021; web:
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >,
                    <a
                        href="https://dveprahy.cz/"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >).
                </p>

                <p>19. století</p>
                <p class="mb-5">
                    1815 – <span class="font-bold">Jüttnerův plán Prahy</span>,
                    taktéž geometricky zaměřený (1 : 4320), všeobecně uznávaný a
                    často historiky využívaný, obsahuje opět názvy ulic v
                    němčině a nová čísla popisná po změně v roce 1805 (Archiv
                    hl. m. Prahy, Sbírka plánů, sign. MAP P 1 B/28,
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >,
                    <a
                        href="https://dveprahy.cz/"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >).
                </p>

                <p class="mb-5">
                    1842–1845 –
                    <span class="font-bold"
                        >Plán Prahy s vedutami v rámu Carla Vasqueze de
                        Pinas/Pinos.</span
                    >
                    Obsahuje opět německé názvy ulic a čísla popisná domů
                    (Archiv hl. m. Prahy, Sbírka plánů, sign., MAP P 2 B/41d).
                </p>

                <p class="mb-5">
                    1840–1856 –
                    <span class="font-bold">Plány Prahy</span> pořízené pro
                    potřeby tzv.
                    <span class="font-bold">Stabilního katastru.</span>
                    Pro vnitřní město (spojenou Prahu 1784) obsahují uliční
                    názvy v němčině a čísla popisná, pro připojené obce jsou
                    cenné zejména indikační skici obsahující množství pomístních
                    názvů (polních tratí), názvů usedlostí, majitelů pozemků
                    apod. V připojených obcích se názvy komunikací objevují
                    zcela výjimečně. Používali jsme pro vnitřní Prahu originální
                    mapu (v měřítku 1: 1440, 1842) a císařský otisk (v měřítku
                    1: 2880, 1852) uložené v Ústředním archivu zeměměřictví a
                    katastru, sign. 5752 a 6068-1, dostupné na
                    <a
                        href="https://ags.cuzk.gov.cz/archiv/"
                        target="_blank"
                        class="article-link"
                        >ags.cuzk.gov.cz/archiv</a
                    >,
                    <a
                        href="https://dveprahy.cz/"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >
                    a
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >, a plán v měřítku 1 : 720 uložený v Institutu plánování a
                    rozvoje hl. m. Prahy (Archiv/spisovna), dostupný na
                    internetových stránkách
                    <a
                        href="https://dveprahy.cz/"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >,
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >, a vydaný tiskem – Lašťovka Marek – Lašťovková Barbora,
                    Plán Prahy a Vyšehradu na základě mapování stabilního
                    katastru (1856), Praha 2008. Pro připojené obce po roce 1784
                    jsme využívali též map císařských otisků a také indikačních
                    skic (<a
                        href="https://ags.cuzk.gov.cz/archiv/"
                        target="_blank"
                        class="article-link"
                        >ags.cuzk.gov.cz/archiv</a
                    >) vesměs z let 1840–1842. K indikačním skicám uloženým v
                    Národním archivu v Praze blíže viz v tiskem vydaném atlasu –
                    Lašťovka Marek – Lašťovková Barbora, Plán Prahy podle
                    indikačních skic stabilního katastru (1840–1842), Praha
                    2005.
                </p>

                <p class="mb-5">
                    1869 –
                    <span class="font-bold"
                        >Polohopisný plán královského města Prahy</span
                    >
                    z roku 1869. Tento plán odráží stav uličního názvosloví po
                    velké změně názvů zachycené v mnohokrát zmíněném Erbenově
                    Autentickém ukazateli. Názvy jsou zde uvedeny v českém i
                    německém jazyce, obsahuje čísla popisná a nově čísla uliční
                    (tzv. modrá, která jsou ovšem na plánu vyznačena červeně),
                    která se v té době stále stala nedílnou součástí základní
                    orientace ve městě (Archiv hl. m. Prahy, Sbírka plánů, sign.
                    MAP P 2 E/87, dostupný též na
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                    >
                        app.iprpraha.cz/apl/app/prahavcera</a
                    >,
                    <a
                        href="https://dveprahy.cz/"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >).
                </p>

                <p class="mb-5">
                    1884 –
                    <span class="font-bold"
                        >Polohopisný plán královského hlavního města Prahy</span
                    >
                    (I. Staré město, II. Nové město, III. Malá Strana, IV.
                    Hradčany, V. Josefov, VI. Vyšehrad) pak Smíchova, Karlína,
                    král. Vinohradů, Žižkova a Nuslí s udáním starých i nových
                    čísel domů. Sestavil Alfred Hurtig, geometr král. hl. města
                    Prahy. Plán je dostupný na portálu www.dveprahy.cz a
                    obsahuje názvy v češtině i němčině, uvedena jsou čísla
                    popisná (dostupný též na
                    <a
                        href="https://dveprahy.cz/"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >
                    a
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >).
                </p>

                <p>20. století</p>
                <p class="mb-5">
                    1908 –
                    <span class="font-bold"
                        >Plán královského hlavního města Prahy a obcí
                        sousedních</span
                    >, Praha 1908 (Storch a syn). Obsahuje pouze názvy ulic v
                    češtině.
                </p>

                <p class="mb-5">
                    1910 –
                    <span class="font-bold">Plan von Prag und Umgebung</span>,
                    Berlin (snad) 1910, obsahuje pouze německé názvy ulic, byť
                    nebyly úřední.
                </p>

                <p class="mb-5">
                    1909–1914 –
                    <span class="font-bold"
                        >Orientační plán hl. m. Prahy a obcí sousedních</span
                    >, kromě českých názvů ulic jsou zde čísla popisná domů a
                    plán zasahuje významně za hranice tehdejšího města Prahy a
                    jeho předměstí (dostupný též na
                    <a
                        href="https://dveprahy.cz/"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >
                    a
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >).
                </p>

                <p class="mb-5">
                    1920–1924 –
                    <span class="font-bold"
                        >Výškopisný plán hlavního města Prahy s okolím</span
                    >
                    obsahuje čísla popisná, názvy ulic, opět zasahuje i za
                    hranice tehdejší, již tzv. Velké Prahy (dostupný též na
                    <a
                        href="https://dveprahy.cz/"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >
                    a
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >).
                </p>

                <p class="mb-5">
                    1938
                    <span class="font-bold"
                        >– Orientační plán hl. m. Prahy s okolím</span
                    >
                    je jedním z nejznámějších a nejpodrobnějších plánů Prahy z
                    období první Československé republiky, kromě názvů ulic
                    obsahuje i čísla popisná, zasahuje i za hranice tehdejší
                    Prahy, avšak mnohdy zachycuje stav ve fázi plánované
                    výstavby – Archiv hlavního města Prahy, Sbírka plánů, sign.
                    MAP P 3/101 dostupný online na
                    <a
                        href="https://dveprahy.cz/"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >,
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >
                    nebo
                    <a
                        href="http://web2.mlp.cz/mapa1938/"
                        target="_blank"
                        class="article-link"
                        >web2.mlp.cz/mapa1938</a
                    >, na stránkách městské knihovny je k němu uveden i rejstřík
                    <a
                        href="https://web2.mlp.cz/mapa1938/rejstriky.php"
                        target="_blank"
                        class="article-link"
                        >web2.mlp.cz/mapa1938/rejstriky.php</a
                    >.
                </p>

                <p class="mb-5">
                    1943/1944 –
                    <span class="font-bold">Plány Prahy Rudolfa Jiříka</span> s
                    uličními názvy – německá (Plan der Haupstadt Prag, kreslil a
                    vydal Rudolf Jiřík, 1943 (Archiv hl. m. Prahy, Sbírka plánů,
                    sign. MAP P 3/6001 –
                    <a
                        href="https://katalog.ahmp.cz/pragapublica/permalink?xid=7E88DEBDFBD511DF822500166F1163D4"
                        target="_blank"
                        class="article-link"
                        >on-line</a
                    >
                    ) a česká verze (Orientační plán hlavního města Prahy,
                    kreslil a vydal Rudolf Jiřík, Praha 1944), obsahují
                    rejstříky ulic, avšak jsou kartograficky velmi nepřesné,
                    spolu s výše psaným úředním seznamem z roku 1940 jsou však
                    nepostradatelné, čísla popisná zde uvedena nejsou.
                </p>

                <p class="mb-5">
                    1948 –
                    <span class="font-bold"
                        >Podrobný plán hlavního města Prahy</span
                    >
                    v měřítku 1: 15 000 na 36 mapových listech zachycuje i
                    relativně vzdálené okolí Prahy včetně názvů ulic, součástí
                    je jejich rejstřík (Podrobný plán hlavního města Prahy,
                    Praha: Melantrich 1948).
                </p>

                <p class="mb-5">
                    1950 –
                    <span class="font-bold">Plán Prahy Rudolfa Jiříka</span> je
                    vhodný ke komparaci s výše uvedenými okupačními plány z let
                    1943/1944 (Orientační plán hlavního města Prahy, kreslil
                    Rudolf Jiřík, Praha 1950 (Archiv hl. m. Prahy, Sbírka plánů,
                    sign. MAP P 3/6215 –

                    <a
                        href="https://katalog.ahmp.cz/pragapublica/permalink?xid=C1E58B202DDF11E094980008744C3219"
                        target="_blank"
                        class="article-link"
                        >on-line</a
                    >).
                </p>

                <p class="mb-5">
                    1964–2000 – <span class="font-bold">plány</span> vydávané
                    nakladatelstvím
                    <span class="font-bold">Kartografie</span> (od konce
                    sedmdesátých do konce devadesátých let 20. století jsou ve
                    stejném formátu a měřítku 1: 20 000 se shodným kladem listů)
                    jsou pro toto období nezastupitelné, obsahují rejstříky
                    ulic, nakladatelství se snažilo udržovat každoročně aktuální
                    stav.
                </p>

                <p>Současnost</p>
                <p class="mb-5">
                    2000 –
                    <span class="font-bold"
                        >mapové servery Institutu plánování a rozvoje
                        města</span
                    ><br />(<a
                        href="https://geoportalpraha.cz"
                        target="_blank"
                        class="article-link"
                        >geoportalpraha.cz</a
                    >,
                    <a
                        href="https://dveprahy.cz"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >
                    a
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >) a Českého úřadu zeměměřického a katastrálního (<a
                        href="https://geoportal.cuzk.cz"
                        target="_blank"
                        class="article-link"
                        >geoportal.cuzk.cz</a
                    >,
                    <a
                        href="https://ags.cuzk.cz/archiv/"
                        target="_blank"
                        class="article-link"
                        >ags.cuzk.cz/archiv</a
                    >. Všechny tyto servery odrážejí oficiální data z Registru
                    územní identifikace, adres a nemovitostí).
                </p>

                <p>Letecké mapy</p>

                <p class="mb-10 sm:mb-25">
                    Letecké mapy podávají informaci zejména o tom, zda nějaká
                    ulice fyzicky existovala v dané době či zda existovala třeba
                    jen v úředně schváleném názvu nebo na plánech. Dochovány
                    jsou z let 1938, 1945, 1953, 1966, 1975, 1988/89, 1996, 2019
                    (přístupné na
                    <a
                        href="https://dveprahy.cz"
                        target="_blank"
                        class="article-link"
                        >dveprahy.cz</a
                    >,
                    <a
                        href="https://app.iprpraha.cz/apl/app/prahavcera"
                        target="_blank"
                        class="article-link"
                        >app.iprpraha.cz/apl/app/prahavcera</a
                    >) 2003, 2006, 2012, 2015 (<a
                        href="https://mapy.com"
                        target="_blank"
                        class="article-link"
                        >mapy.com</a
                    >).
                </p>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <nuxt-link
                            to="/clanky/prameny-seznamy-ulic"
                            class="flex justify-start underline"
                        >
                            <img
                                src="../../public/img/left-arrow.png"
                                alt=""
                                class="order-1 mr-2 inline h-5 w-5"
                            />
                            <span class="order-2 mr-2"
                                >Prameny – seznamy ulic</span
                            >
                        </nuxt-link>
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <nuxt-link
                            to="/clanky/ostatni-prameny"
                            class="flex justify-end underline"
                        >
                            <span class="order-1 ml-2">Ostatní prameny</span>
                            <img
                                src="../../public/img/right-arrow.png"
                                alt=""
                                class="order-2 ml-2 inline h-5 w-5"
                            />
                        </nuxt-link>
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts"></script>

<style scoped></style>
